import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { toast } from 'sonner';
import { quotationIntakeService } from '@features/quotation-intake/services';
import type {
  BaseMarkup,
  MatchedQuotationGroup,
  MatchingResult,
  RematchLineItemRequest,
} from '@features/quotation-intake/types/quotationIntake.types';
import {
  DEFAULT_PRICE_LEVEL,
  EmailExtractionStatus,
  PickupType,
  QuotationStatus,
  WaivedFreight,
  type ExtractionNotice,
  type LineItemSpecSnapshot,
  type QuotationDraft,
  type QuotationLineItemDto,
} from '../types/quotation.types';
import { quotationService } from '../services/quotationService';
import { dropQuotationKilogramRows, isSpecValidForRematch } from '../utils/lineItem';
import {
  adoptableRematchQuantity,
  isUnchangedSince,
  roundedHint,
  roundedQuantityFor,
  withSource,
} from '../utils/cuttingListQuantity';

// A single intake submission — pasted text and/or one-or-more files/screenshots, extracted
// together as one deduped set (86d3hdy52). `files` is empty for a text-only submission.
export type IntakeSource = { text: string; files: File[] };

/** Names of the files in a source, joined + capped, for the draft's extractionFileName. */
function joinFileNames(files: File[], max = 256): string | null {
  if (files.length === 0) return null;
  const joined = files.map((f) => f.name).join(', ');
  return joined.length <= max ? joined : `${joined.slice(0, max - 1)}…`;
}

function uid(): string {
  return `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

export function createBlankDraft(source: 'manual' | 'intake' = 'manual'): QuotationDraft {
  const today = new Date();
  const plus30 = addDays(today, 30);
  const now = today.toISOString();

  return {
    id: uid(),
    serverId: undefined,
    status: QuotationStatus.Draft,
    customerName: '',
    headMainOffice: '',
    customerId: null,
    customerEntityId: null,
    headMainOfficeCustomerId: null,
    projectCategoryId: null,
    projectCategoryName: null,
    projectName: '',
    termsId: null,
    termsName: null,
    salesRepId: null,
    salesCoordId: null,
    taxRate: null,
    date: isoDate(today),
    escalationDate: isoDate(plus30),
    expirationDate: isoDate(plus30),
    pickup: PickupType.No,
    waivedFreight: WaivedFreight.No,
    // 86d32r9rv — freight-charge JSON columns. Brand-new local drafts start with
    // all three null; they're populated by save echo once the salesperson opens
    // the freight cabinet and makes a selection.
    freightInputDraftJson: null,
    freightChargeJson: null,
    freightSnapshotJson: null,
    lineItems: [],
    extractionStatus: 'idle',
    extractionError: null,
    extractionSource: source,
    extractionSourceKind: null,
    extractionText: null,
    extractionFileName: null,
    extractionNotice: null,
    createdAt: now,
    updatedAt: now,
    dirty: false,
    sourceEmail: null,
    attachments: [],
    emailExtractionStatus: EmailExtractionStatus.NotApplicable,
    isArchived: false,
    archivedAt: null,
    archivedById: null,
  };
}

/** Files cannot be persisted server-side today. Kept in memory so extraction retry still works. */
const pendingFiles = new Map<string, File[]>();

export function canRetryExtraction(draft: QuotationDraft): boolean {
  if (draft.extractionStatus !== 'failed') return false;
  if (draft.extractionSourceKind === 'text') return !!draft.extractionText;
  if (draft.extractionSourceKind === 'file') return (pendingFiles.get(draft.id)?.length ?? 0) > 0;
  return false;
}

export function getPendingFile(draftId: string): File | null {
  return pendingFiles.get(draftId)?.[0] ?? null;
}

export function mapMatchedToLineItems(quotations: MatchedQuotationGroup[]): QuotationLineItemDto[] {
  return quotations.flatMap((g, gi) =>
    g.lineItems.map((li, idx) => {
      const unit: 'pcs' | 'kgs' = li.unit === 'kgs' ? 'kgs' : 'pcs';
      const gradeCode = li.gradeCode != null ? String(li.gradeCode) : null;
      // QUO-R-004 (14ykpqtw3hw): quantity/unit are the server-NORMALIZED value, so a rounded or converted
      // line is not born "Edited" and Revert lands on whole pieces; the customer's value rides alongside.
      const originalSpec: LineItemSpecSnapshot = {
        diameterMm: li.diameterMm,
        lengthM: li.lengthM,
        gradeCode,
        isWeldable: li.isWeldable,
        quantity: li.quantity,
        unit,
        ...(li.sourceQuantity != null && li.sourceUnit
          ? { sourceQuantity: li.sourceQuantity, sourceUnit: li.sourceUnit === 'kgs' ? 'kgs' : 'pcs' }
          : {}),
      };
      return {
        id: `x-${gi}-${idx}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`,
        diameterMm: li.diameterMm,
        lengthM: li.lengthM,
        gradeCode,
        isWeldable: li.isWeldable,
        quantity: li.quantity,
        unit,
        weightKg: li.totalWeightKg,
        matchStatus: isLineItemMatchStatus(li.matchStatus) ? li.matchStatus : undefined,
        rawDescription: li.rawDescription,
        notes: li.notes,
        matchedSalesDescription: li.matchedSalesDescription,
        matchedItemId: li.matchedItemId,
        priceLevel: DEFAULT_PRICE_LEVEL,
        basePricePerPc: li.basePricePerPc,
        averagePricePerPc: li.averagePricePerPc,
        possibleMatches: li.possibleMatches?.map((pm) => ({
          itemId: pm.itemId,
          lengthM: pm.lengthM,
          salesDescription: pm.salesDescription,
          weightPerPcKg: pm.weightPerPcKg,
        })),
        originalSpec,
        isEdited: false,
        // CSC-291: server already set this when GradePromotionRules fired. Frontend
        // surfaces it as a per-line badge + the aggregate banner below.
        promotedFromGrade: li.promotedFromGrade,
      };
    })
  );
}

const SPEC_FIELDS = ['diameterMm', 'lengthM', 'gradeCode', 'isWeldable', 'quantity', 'unit'] as const;

function snapshotFromItem(item: QuotationLineItemDto): LineItemSpecSnapshot {
  return {
    diameterMm: item.diameterMm,
    lengthM: item.lengthM,
    gradeCode: item.gradeCode,
    isWeldable: item.isWeldable,
    quantity: item.quantity,
    unit: item.unit,
  };
}

function differsFromOriginal(
  original: LineItemSpecSnapshot | null | undefined,
  current: LineItemSpecSnapshot,
): boolean {
  if (!original) return false;
  return (
    original.diameterMm !== current.diameterMm ||
    original.lengthM !== current.lengthM ||
    original.gradeCode !== current.gradeCode ||
    original.isWeldable !== current.isWeldable ||
    original.quantity !== current.quantity ||
    original.unit !== current.unit
  );
}

// 86d3dxnd7: gate on the server validator's rules (valid grade + positive
// diameter/length), not mere presence — else an invalid spec fires a re-match
// that 400s. Shared predicate keeps client + server in lock-step.
function requiredSpecsSet(item: QuotationLineItemDto): boolean {
  return isSpecValidForRematch(item);
}

/** Per-(draft,item) pending debounced re-match timers. */
const rematchTimers = new Map<string, ReturnType<typeof setTimeout>>();

function rematchKey(draftId: string, itemId: string): string {
  return `${draftId}:${itemId}`;
}

const MATCH_STATUS_VALUES = new Set<string>([
  'MATCHED',
  'NEEDS_CLARIFICATION',
  'NO_MATCH',
  'SUMMARY_ONLY',
  'EXTERNAL_ATTACHMENT',
]);

function isLineItemMatchStatus(value: string): value is QuotationLineItemDto['matchStatus'] & string {
  return MATCH_STATUS_VALUES.has(value);
}

function applyExtractionResult(draft: QuotationDraft, result: MatchingResult): QuotationDraft {
  const clientName = result.clientInfo?.company_name?.trim() ?? '';
  const projectName = result.projectInfo?.project_name?.trim() ?? '';
  // 14ykpqtr22u — cutting lists take pieces only: a kg FIGURE never becomes a row. 14ykpqtw3hw: the
  // quotation route already dropped repeats and totals and converted kg orders (QUO-R-003); what still
  // reads kg here is a figure (dropped) or an unconvertible order (kept, unmatched).
  const { rows: lineItems, droppedKgLineCount: kgFiguresDropped } = dropQuotationKilogramRows(
    mapMatchedToLineItems(result.quotations),
  );
  const repeats = result.kilogramRepeatsDropped ?? 0;
  const totals = result.kilogramTotalsDropped ?? 0;
  return {
    ...draft,
    customerName: draft.customerName || clientName,
    headMainOffice: draft.headMainOffice || clientName,
    projectName: draft.projectName || projectName,
    lineItems,
    droppedKgLineCount: kgFiguresDropped + repeats + totals,
    extractionStatus: 'completed',
    extractionError: null,
    extractionNotice: deriveExtractionNotice(result, kgFiguresDropped, lineItems.length),
    updatedAt: new Date().toISOString(),
  };
}

export function deriveExtractionNotice(
  result: MatchingResult,
  kgFiguresDropped = 0,
  keptLineCount = 1,
): ExtractionNotice | null {
  if (result.quantityDefaultedToOne && result.itemsDefaultedToOneCount > 0) {
    return { kind: 'all-defaulted', count: result.itemsDefaultedToOneCount };
  }
  if (result.itemsDroppedForMissingQuantity > 0) {
    return { kind: 'partial-dropped', count: result.itemsDroppedForMissingQuantity };
  }
  // 14ykpqtw3hw — the one notice slot keeps the kg-drop reason; the per-line "asked …" note is the primary
  // surface for rounding and conversion. With nothing left the "Only kilogram quantities" note says it instead.
  const repeats = result.kilogramRepeatsDropped ?? 0;
  const totals = result.kilogramTotalsDropped ?? 0;
  if (keptLineCount > 0 && repeats + totals + kgFiguresDropped > 0) {
    return { kind: 'kg-dropped', repeats, totals, figures: kgFiguresDropped };
  }
  // CSC-291: aggregate per-line promotions (set by GradePromotionRules on the server)
  // into a single banner notice. The matched-line items carry the pre-promotion grade
  // alongside the current (post-promotion) grade; group by `(from, to)` pair.
  const promoted = result.quotations
    .flatMap((g) => g.lineItems)
    .filter((li) => li.promotedFromGrade != null && li.gradeCode != null);
  if (promoted.length > 0) {
    return { kind: 'grades-promoted', promotions: aggregateGradePromotions(promoted.map((li) => ({
      from: li.promotedFromGrade as number,
      to: li.gradeCode as number,
    }))) };
  }
  return null;
}

/**
 * CSC-291: same banner-notice derivation as `deriveExtractionNotice`, but operates on a
 * hydrated `QuotationDraft` so it works on AutoDraft re-opens (where there's no live
 * `MatchingResult`). Keeps the banner consistent across paths.
 */
function deriveDraftPromotionNotice(draft: QuotationDraft): ExtractionNotice | null {
  const promoted = draft.lineItems
    .filter((li) => li.promotedFromGrade != null && li.gradeCode != null)
    .map((li) => ({
      from: li.promotedFromGrade as number,
      to: Number(li.gradeCode as string),
    }))
    .filter((p) => Number.isFinite(p.to));
  if (promoted.length === 0) return null;
  return { kind: 'grades-promoted', promotions: aggregateGradePromotions(promoted) };
}

function aggregateGradePromotions(
  pairs: Array<{ from: number; to: number }>,
): Array<{ from: number; to: number; count: number }> {
  const counts = new Map<string, { from: number; to: number; count: number }>();
  for (const p of pairs) {
    const key = `${p.from}->${p.to}`;
    const existing = counts.get(key);
    if (existing) existing.count++;
    else counts.set(key, { from: p.from, to: p.to, count: 1 });
  }
  return Array.from(counts.values()).sort((a, b) => a.from - b.from || a.to - b.to);
}

/** Strip transient UI flags before sending to the server — they're not meaningful persisted. */
function stripTransient(draft: QuotationDraft): QuotationDraft {
  return {
    ...draft,
    lineItems: draft.lineItems.map((li) => ({ ...li, isMatching: false, matchError: null })),
  };
}

interface QuotationDraftState {
  drafts: QuotationDraft[];
  activeId: string | null;
  activeView: 'intake' | 'draft';
  customerColRatio: number;
  customerPanelCollapsed: boolean;
  reviewMode: boolean;
  reviewSourceColRatio: number;
  // Tracks which attachment the EmailReviewSourcePanel is currently displaying in the
  // preview viewer. Single-valued: only one file shows at a time. Reset on draft load.
  reviewActiveAttachmentId: number | null;

  // CSC-283 Layer 3 multi-source extraction: the *set* of attachment ids the retry
  // button feeds to ExtractFromEmailAsync. Distinct from reviewActiveAttachmentId
  // (which is just the preview pane's pointer). null = "use backend default" (all
  // real / non-signature attachments). Empty array = "body only, no attachments."
  reviewSelectedAttachmentIds: number[] | null;

  // Save lifecycle — surfaced to UI so we can show "Saving…" / "Saved" status.
  isSaving: boolean;
  saveError: string | null;
  lastSavedAt: string | null;

  /** True when reflecting server state, not a local view of a yet-unsaved edit. */
  isLoadingFromServer: boolean;

  addBlank: () => string;
  update: (id: string, patch: Partial<Omit<QuotationDraft, 'id' | 'createdAt'>>) => void;
  setLineItems: (id: string, lineItems: QuotationLineItemDto[]) => void;
  updateLineItem: (draftId: string, itemId: string, patch: Partial<QuotationLineItemDto>) => void;
  addLineItem: (draftId: string) => string;
  removeLineItem: (draftId: string, itemId: string) => void;
  rematchLineItem: (draftId: string, itemId: string) => Promise<void>;
  remove: (id: string) => string | null;
  activate: (id: string) => void;
  /** Clear `activeId` without evicting drafts. Used when the workspace surface
   *  navigates away from a saved draft (URL id removed, master-list row dropped
   *  after archive/delete/restore) so the detail panel falls to EmptyState
   *  instead of rendering the now-stale active draft. */
  clearActive: () => void;
  showIntake: () => void;
  clear: () => void;
  setCustomerColRatio: (ratio: number) => void;
  setCustomerPanelCollapsed: (collapsed: boolean) => void;
  setReviewMode: (enabled: boolean) => void;
  setReviewSourceColRatio: (ratio: number) => void;
  setReviewActiveAttachmentId: (id: number | null) => void;
  /** Replace the selected-attachment set wholesale. Pass null to fall back to backend default. */
  setReviewSelectedAttachmentIds: (ids: number[] | null) => void;
  /** Toggle a single id in the selected set. Initializes from `defaultIds` when current state is null. */
  toggleReviewSelectedAttachmentId: (id: number, defaultIds: number[]) => void;

  /** Upserts a draft (matched by serverId when present, otherwise by local id) and activates it.
   *
   *  When the existing local copy is dirty AND the incoming draft is editable
   *  (Draft / AutoDraft), preserves the user's unsaved header + freight inputs
   *  while taking lineItems + server-authoritative metadata from the payload.
   *  This guards against a server re-fetch (retry-extraction, stale-notice reload)
   *  silently wiping a just-ticked Within Metro Manila checkbox.
   *
   *  Pass `{ force: true }` from deliberate-overwrite call sites (stale-notice
   *  Reload after the user confirms) to bypass the merge and accept the server
   *  payload wholesale.
   *
   *  Submitted / Sent incoming drafts always do a full overwrite — those rows
   *  are immutable on NetSuite and any local pending edits are meaningless. */
  loadDraft: (draft: QuotationDraft, options?: { force?: boolean }) => void;

  /** Closes the local tab for a draft without touching the server. Returns the next active id (or null). */
  closeTab: (id: string) => string | null;

  /** Stamp fresh posting metadata onto the active draft after a successful cutting-list submit. */
  markSubmitted: (draftId: string, posting: { cuttingListNumber: string; netSuiteUrl: string; postedAt: string; postedByName: string | null; newQuotationNumber?: string }) => void;

  /** 86d3jkwuq async posting — serverIds this session claimed for posting. The panel's SSE
   *  listener treats a terminal event (Submitted / PostingFailed) for one of these as an OWN-submit
   *  result (in-place refresh + toast) rather than a stale-record notice. */
  postingDraftIds: Set<number>;
  /** Optimistic flip to Posting on a successful 202 claim (badge/spinner shows before the SSE echo)
   *  + start tracking the serverId. */
  markPosting: (serverId: number) => void;
  /** Stop tracking a serverId once its terminal posting event has been consumed. */
  ackPosting: (serverId: number) => void;

  /**
   * CSC-290: persist the server-recomputed grade markups + base-markup snapshot
   * onto the active draft after a successful cutting-list submit. Re-opening the
   * row hydrates from these (via the GET /quotations/{id} pipeline) so the user
   * sees what was actually quoted to NetSuite, not what their stale workspace had
   * computed locally.
   *
   * Per-line rate / rateWithMarkup are NOT stored on QuotationLineItemDto today —
   * the line items table recomputes from `baseMarkup × line` at render time. The
   * action accepts `recomputedLines` for forward-compatibility and so callers can
   * detect drift; current implementation does not patch per-line state.
   */
  applyServerRecompute: (draftId: string, payload: {
    baseMarkupPct: number;
    gradeMarkups: { grade33: number; grade40: number; grade60: number; grade75: number; grade80: number };
    recomputedLines: Array<{ matchedItemId: string; rate: number | null; rateWithMarkup: number | null }>;
  }) => void;

  /**
   * CSC-291: optimistically flip the persisted `emailExtractionStatus` so the
   * spinner badge sticks across navigation while a retry is in flight. Caller
   * sets Pending before awaiting the retry; on success `loadDraft(updated)`
   * overwrites with the server's verdict, on failure caller sets Failed locally.
   * If the user closes the tab mid-retry the optimistic state lives only in
   * sessionStorage and gets reconciled by the next master-list refresh.
   */
  setEmailExtractionStatus: (draftId: string, status: EmailExtractionStatus) => void;

  /** Persists the draft to the server on demand. Pass the panel-mount baseMarkup so
   *  the save includes the Phase-3 PricingSnapshot drift baseline.
   *
   *  86d32t0xr: `options.silent=true` is set by the auto-save debouncer in
   *  QuotationDetailPanel. Silent mode (a) suppresses the failure toast (the
   *  status chip surfaces the error instead), and (b) skips the tick when
   *  another save is already in flight (`isSaving=true`) so the next debounce
   *  window catches up rather than stacking. Manual Save (no options) ignores
   *  the in-flight guard so the button always feels responsive.
   *
   *  Returns the updated in-store draft, or null if the save was skipped or failed. */
  saveDraft: (
    draftId?: string,
    baseMarkup?: BaseMarkup | null,
    options?: { silent?: boolean },
  ) => Promise<QuotationDraft | null>;

  startExtraction: (source: IntakeSource) => string;
  extractIntoDraft: (draftId: string, source: IntakeSource) => void;
  retryExtraction: (draftId: string) => boolean;
  dismissExtractionNotice: (draftId: string) => void;
}

const MIN_CUSTOMER_COL_RATIO = 0.18;
const MAX_CUSTOMER_COL_RATIO = 0.55;
const DEFAULT_CUSTOMER_COL_RATIO = 0.28;

function clampCustomerColRatio(v: number): number {
  if (!Number.isFinite(v)) return DEFAULT_CUSTOMER_COL_RATIO;
  return Math.max(MIN_CUSTOMER_COL_RATIO, Math.min(MAX_CUSTOMER_COL_RATIO, v));
}

const MIN_REVIEW_SOURCE_COL_RATIO = 0.6;
const MAX_REVIEW_SOURCE_COL_RATIO = 0.85;
const DEFAULT_REVIEW_SOURCE_COL_RATIO = 0.75;

function clampReviewSourceColRatio(v: number): number {
  if (!Number.isFinite(v)) return DEFAULT_REVIEW_SOURCE_COL_RATIO;
  return Math.max(MIN_REVIEW_SOURCE_COL_RATIO, Math.min(MAX_REVIEW_SOURCE_COL_RATIO, v));
}

export const useQuotationDraftStore = create<QuotationDraftState>()(
  persist(
    (set, get) => {
  // Mark a draft dirty in the store. Called from every mutation that needs to surface
  // an "Unsaved changes" indicator and gate the Save Draft button.
  const markDirty = (draftLocalId: string) => {
    set((state) => ({
      drafts: state.drafts.map((d) =>
        d.id === draftLocalId && !d.dirty ? { ...d, dirty: true } : d,
      ),
    }));
  };

  const scheduleRematch = (draftId: string, itemId: string) => {
    const key = rematchKey(draftId, itemId);
    const existing = rematchTimers.get(key);
    if (existing) clearTimeout(existing);
    const handle = setTimeout(() => {
      rematchTimers.delete(key);
      void get().rematchLineItem(draftId, itemId);
    }, 250);
    rematchTimers.set(key, handle);
  };

  const runExtraction = (draftId: string, source: IntakeSource) => {
    quotationIntakeService
      .extractCombined(source.text, source.files)
      .then((result) => {
        set((state) => ({
          drafts: state.drafts.map((d) =>
            d.id === draftId ? applyExtractionResult(d, result) : d
          ),
        }));
        // Extraction results are unsaved work — surface them in the Unsaved indicator until
        // the user clicks Save Draft.
        markDirty(draftId);
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Extraction failed';
        set((state) => ({
          drafts: state.drafts.map((d) =>
            d.id === draftId
              ? { ...d, extractionStatus: 'failed', extractionError: msg, updatedAt: new Date().toISOString() }
              : d
          ),
        }));
      });
  };

  return {
    drafts: [],
    activeId: null,
    activeView: 'intake',
    customerColRatio: DEFAULT_CUSTOMER_COL_RATIO,
    customerPanelCollapsed: false,
    reviewMode: false,
    reviewSourceColRatio: DEFAULT_REVIEW_SOURCE_COL_RATIO,
    reviewActiveAttachmentId: null,
    reviewSelectedAttachmentIds: null,
    isSaving: false,
    saveError: null,
    lastSavedAt: null,
    isLoadingFromServer: false,
    postingDraftIds: new Set<number>(),

    setCustomerColRatio: (ratio) => set({ customerColRatio: clampCustomerColRatio(ratio) }),
    setCustomerPanelCollapsed: (collapsed) => set({ customerPanelCollapsed: collapsed }),
    setReviewMode: (enabled) => set({ reviewMode: enabled }),
    setReviewSourceColRatio: (ratio) =>
      set({ reviewSourceColRatio: clampReviewSourceColRatio(ratio) }),

    setReviewActiveAttachmentId: (id) => set({ reviewActiveAttachmentId: id }),

    setReviewSelectedAttachmentIds: (ids) => set({ reviewSelectedAttachmentIds: ids }),

    toggleReviewSelectedAttachmentId: (id, defaultIds) => {
      const current = get().reviewSelectedAttachmentIds ?? defaultIds;
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      set({ reviewSelectedAttachmentIds: next });
    },

    addBlank: () => {
      const blank = createBlankDraft('manual');
      set((state) => ({
        drafts: [...state.drafts, blank],
        activeId: blank.id,
        activeView: 'draft',
      }));
      return blank.id;
    },

    loadDraft: (draft, options) => {
      set((state) => {
        // Match by serverId first (authoritative once saved), fall back to local id.
        const match = (d: QuotationDraft) => {
          if (draft.serverId != null && d.serverId === draft.serverId) return true;
          return d.id === draft.id;
        };
        const existingIdx = state.drafts.findIndex(match);
        const existing = existingIdx >= 0 ? state.drafts[existingIdx] : null;
        // CSC-291: derive the grade-promotion banner from the hydrated line items so
        // AutoDrafts (and reload-after-save manual drafts) fire the notice on first open.
        // Don't clobber an existing notice — once dismissed it should stay dismissed.
        const promotionNotice = draft.extractionNotice ?? deriveDraftPromotionNotice(draft);

        // Submitted / Sent incoming drafts are immutable on NetSuite — any local
        // copy is a cache that could be stale (post-submit corrections, freight
        // snapshot rebuild). Always rebuild from the server payload, ignoring the
        // dirty state which is meaningless for read-only rows.
        const incomingIsTerminal =
          draft.status === QuotationStatus.Submitted || draft.status === QuotationStatus.Sent;

        // Preserve unsaved user edits when reloading an editable row. Without
        // this, a re-fetch landing between (e.g.) toggling Within Metro Manila
        // and clicking Save would silently revert the toggle — freezing the
        // wrong BillingMode into the freight snapshot at submit. Deliberate
        // overwrite call sites (stale-notice Reload after confirm) pass
        // `force: true` to bypass.
        const shouldPreserveLocalEdits =
          !options?.force && !incomingIsTerminal && existing != null && existing.dirty;

        const fresh: QuotationDraft = shouldPreserveLocalEdits
          ? {
              // Inversion pattern: spread incoming first so new server-authoritative
              // fields (added by future schema additions) are picked up automatically,
              // then re-apply the user-editable subset from local. Listing fields
              // explicitly here makes the preserved surface auditable.
              ...draft,
              dirty: true,
              // extractionNotice Option A: prefer local dismissal (existing was null
              // because the user dismissed), fall back to re-derivation against the
              // incoming line items so new promotions still surface.
              extractionNotice: existing!.extractionNotice ?? deriveDraftPromotionNotice(draft),
              // Header
              customerName: existing!.customerName,
              customerId: existing!.customerId,
              customerEntityId: existing!.customerEntityId,
              headMainOffice: existing!.headMainOffice,
              headMainOfficeCustomerId: existing!.headMainOfficeCustomerId,
              projectCategoryId: existing!.projectCategoryId,
              projectCategoryName: existing!.projectCategoryName,
              projectName: existing!.projectName,
              termsId: existing!.termsId,
              termsName: existing!.termsName,
              salesRepId: existing!.salesRepId,
              salesCoordId: existing!.salesCoordId,
              taxRate: existing!.taxRate,
              date: existing!.date,
              escalationDate: existing!.escalationDate,
              expirationDate: existing!.expirationDate,
              pickup: existing!.pickup,
              waivedFreight: existing!.waivedFreight,
              // Freight inputs — the specific surface this protection was added for.
              // 86d32r9rv: serialized as a single JSON blob; cabinet parses on render.
              freightInputDraftJson: existing!.freightInputDraftJson,
              // Markup is panel-edited; keep local until save round-trips it back.
              baseMarkupPct: existing!.baseMarkupPct,
              gradeMarkupsJson: existing!.gradeMarkupsJson,
            }
          : {
              ...draft,
              dirty: false,
              extractionNotice: promotionNotice ?? draft.extractionNotice,
            };
        const nextDrafts =
          existingIdx >= 0
            ? state.drafts.map((d, i) => (i === existingIdx ? fresh : d))
            : [...state.drafts, fresh];
        return {
          drafts: nextDrafts,
          activeId: fresh.id,
          activeView: 'draft',
        };
      });
    },

    markSubmitted: (draftId, posting) => {
      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? {
                ...d,
                status: QuotationStatus.Submitted,
                cuttingListNumber: posting.cuttingListNumber,
                netSuiteUrl: posting.netSuiteUrl,
                postedAt: posting.postedAt,
                postedByName: posting.postedByName,
                // CSC-293: keep the local quotationNumber in sync with the
                // backend's submit-time DR→QD swap so the panel + summary
                // band re-render with QD- without an extra round-trip.
                // Falls back to the existing value when the server didn't
                // return a new number (partial-success path).
                quotationNumber: posting.newQuotationNumber && posting.newQuotationNumber.length > 0
                  ? posting.newQuotationNumber
                  : d.quotationNumber,
                // 86d332med — stamp the freight snapshot locally from the just-submitted
                // freightChargeJson. Server-side, CuttingListService writes FreightSnapshotJson
                // via JsonSerializer.Serialize(freightCharge) where `freightCharge` was parsed
                // from the SAME FreightChargeJson the client already has. The two columns
                // hold equivalent JSON post-submit. Without this copy, the Submitted view's
                // FreightChargeSummary reads parseFreightCharge(null) → "No freight charge on
                // this quotation" until manual refresh, because the submit response surfaces
                // only posting metadata (cuttingListNumber, etc.) and not the snapshot field.
                freightSnapshotJson: d.freightChargeJson,
                // Submission implies a successful save preceded it — clean by definition.
                dirty: false,
              }
            : d,
        ),
      }));
    },

    markPosting: (serverId) => {
      // 86d3mnuxe — no entity Posting status; postingDraftIds is the optimistic in-flight signal (the
      // panel keys its read-only + posting card off the outbox status + this set).
      set((state) => ({
        postingDraftIds: new Set(state.postingDraftIds).add(serverId),
      }));
    },

    ackPosting: (serverId) => {
      set((state) => {
        if (!state.postingDraftIds.has(serverId)) return {};
        const next = new Set(state.postingDraftIds);
        next.delete(serverId);
        return { postingDraftIds: next };
      });
    },

    applyServerRecompute: (draftId, payload) => {
      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? {
                ...d,
                baseMarkupPct: payload.baseMarkupPct,
                gradeMarkupsJson: JSON.stringify(payload.gradeMarkups),
              }
            : d,
        ),
      }));
    },

    setEmailExtractionStatus: (draftId, status) => {
      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId ? { ...d, emailExtractionStatus: status } : d,
        ),
      }));
    },

    saveDraft: async (draftLocalId, baseMarkup, options) => {
      const id = draftLocalId ?? get().activeId;
      if (!id) return null;
      const draft = get().drafts.find((d) => d.id === id);
      if (!draft) return null;
      // Submitted drafts are immutable.
      if (draft.status === QuotationStatus.Submitted) return null;
      // Don't save while extraction is in flight — the completion handler manages state.
      if (draft.extractionStatus === 'processing') return null;

      // 86d32t0xr — silent (auto-save) ticks skip when a save is already in flight.
      // The next debounce window will catch up because `dirty` stays true until
      // some save successfully resets it. Manual saves don't take this branch:
      // the user clicked the button, they expect something to happen.
      if (options?.silent && get().isSaving) return null;

      set({ isSaving: true, saveError: null });
      try {
        const saved = await quotationService.saveDraft(stripTransient(draft), baseMarkup ?? null);
        // Merge server-canonical freight fields the server computes/normalizes on save.
        //   • freightChargeJson — produced by FreightChargeFactory; the submit-gate
        //     and the workspace freight badge read this. Without merging it back,
        //     the local store stays at null while the cabinet's local allocator shows
        //     a valid total, and the gate falsely reports "freight is partially filled."
        //   • freightInputDraftJson — re-serialized through the canonical
        //     FreightInputDraftDto shape; merging keeps client and server byte-aligned.
        // Other fields stay locally-authoritative (line items, customer details, etc.)
        // because the user may have edited them between save firing and response landing.
        set((state) => ({
          drafts: state.drafts.map((d) =>
            d.id === id
              ? {
                  ...d,
                  serverId: saved.serverId,
                  quotationNumber: saved.quotationNumber,
                  status: saved.status,
                  updatedAt: saved.updatedAt ?? new Date().toISOString(),
                  freightChargeJson: saved.freightChargeJson,
                  freightInputDraftJson: saved.freightInputDraftJson,
                  dirty: false,
                }
              : d,
          ),
          isSaving: false,
          lastSavedAt: new Date().toISOString(),
        }));
        return get().drafts.find((d) => d.id === id) ?? null;
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to save draft';
        set({ isSaving: false, saveError: msg });
        // 86d32t0xr — auto-save is silent on failure; the status chip surfaces
        // the error. Manual saves keep the existing toast.
        if (!options?.silent) toast.error(`Save failed: ${msg}`);
        return null;
      }
    },

    update: (id, patch) => {
      const { drafts } = get();
      const target = drafts.find((d) => d.id === id);
      if (!target) return;
      if (target.status === QuotationStatus.Submitted) return; // Read-only; reject silently.

      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === id ? { ...d, ...patch, dirty: true, updatedAt: new Date().toISOString() } : d,
        ),
      }));
    },

    setLineItems: (id, lineItems) => {
      const { drafts } = get();
      const target = drafts.find((d) => d.id === id);
      if (!target || target.status === QuotationStatus.Submitted) return;

      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === id ? { ...d, lineItems, dirty: true, updatedAt: new Date().toISOString() } : d,
        ),
      }));
    },

    updateLineItem: (draftId, itemId, patch) => {
      const draft = get().drafts.find((d) => d.id === draftId);
      if (!draft || draft.status === QuotationStatus.Submitted) return;
      const current = draft.lineItems.find((li) => li.id === itemId);
      if (!current) return;

      const next: QuotationLineItemDto = { ...current, ...patch };

      // QUO-R-005 (14ykpqtw3hw) — a decimal on a cutting-list piece line rounds up on commit, instantly (the web
      // mirror of QUO-R-002; the re-match below stays authoritative). One place for every path: the table and
      // review-list Qty cells commit on blur, bulk edit applies through here, and a spec edit that completes a
      // line with a fractional quantity rounds it too. Never on load and never at save.
      const touchesSpec = SPEC_FIELDS.some((f) => Object.prototype.hasOwnProperty.call(patch, f));
      const rounded = touchesSpec ? roundedQuantityFor(next) : null;
      if (rounded) {
        next.quantity = rounded.quantity;
        // The customer's own value, if that is what got rounded (QUO-R-004) — never a value sales typed.
        next.originalSpec = withSource(next.originalSpec, rounded.from, 'pcs');
        // A fixed id: a bulk edit across many rows shows one hint, not one per row.
        toast.info(roundedHint(rounded.quantity), { id: 'quo-r-005-rounded-up' });
      }

      if (next.originalSpec) {
        next.isEdited = differsFromOriginal(next.originalSpec, snapshotFromItem(next));
      }

      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? {
                ...d,
                lineItems: d.lineItems.map((li) => (li.id === itemId ? next : li)),
                dirty: true,
                updatedAt: new Date().toISOString(),
              }
            : d,
        ),
      }));

      if (touchesSpec && requiredSpecsSet(next)) {
        scheduleRematch(draftId, itemId);
      }
    },

    addLineItem: (draftId) => {
      const draft = get().drafts.find((d) => d.id === draftId);
      if (!draft || draft.status === QuotationStatus.Submitted) return '';

      const newItem: QuotationLineItemDto = {
        id: `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
        diameterMm: null,
        lengthM: null,
        gradeCode: null,
        isWeldable: false,
        quantity: 1,
        unit: 'pcs',
        weightKg: null,
        matchStatus: 'NEEDS_CLARIFICATION',
        rawDescription: null,
        notes: null,
        matchedSalesDescription: null,
        matchedItemId: null,
        priceLevel: DEFAULT_PRICE_LEVEL,
        basePricePerPc: null,
        averagePricePerPc: null,
        possibleMatches: [],
        originalSpec: null,
        isEdited: false,
      };
      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? {
                ...d,
                lineItems: [...d.lineItems, newItem],
                dirty: true,
                updatedAt: new Date().toISOString(),
              }
            : d,
        ),
      }));
      return newItem.id;
    },

    removeLineItem: (draftId, itemId) => {
      const draft = get().drafts.find((d) => d.id === draftId);
      if (!draft || draft.status === QuotationStatus.Submitted) return;

      const key = rematchKey(draftId, itemId);
      const pending = rematchTimers.get(key);
      if (pending) {
        clearTimeout(pending);
        rematchTimers.delete(key);
      }
      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? {
                ...d,
                lineItems: d.lineItems.filter((li) => li.id !== itemId),
                dirty: true,
                updatedAt: new Date().toISOString(),
              }
            : d,
        ),
      }));
    },

    rematchLineItem: async (draftId, itemId) => {
      const draft = get().drafts.find((d) => d.id === draftId);
      if (!draft || draft.status === QuotationStatus.Submitted) return;
      const item = draft.lineItems.find((li) => li.id === itemId);
      if (!item || !requiredSpecsSet(item)) return;

      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? {
                ...d,
                lineItems: d.lineItems.map((li) =>
                  li.id === itemId ? { ...li, isMatching: true, matchError: null } : li,
                ),
              }
            : d,
        ),
      }));

      const request: RematchLineItemRequest = {
        diameterMm: item.diameterMm!,
        lengthM: item.lengthM!,
        gradeCode: Number(item.gradeCode),
        isWeldable: item.isWeldable,
        quantity: item.quantity ?? undefined,
        unit: item.unit,
        rawDescription: item.rawDescription ?? null,
        notes: item.notes ?? null,
      };

      // What this request carried — the stale-reply guard below compares against it.
      const sent = { quantity: item.quantity ?? null, unit: item.unit };

      try {
        const result = await quotationIntakeService.rematchLineItem(request);
        const nextStatus = isLineItemMatchStatus(result.matchStatus) ? result.matchStatus : undefined;
        set((state) => ({
          drafts: state.drafts.map((d) =>
            d.id === draftId
              ? {
                  ...d,
                  lineItems: d.lineItems.map((li) => {
                    if (li.id !== itemId) return li;
                    // 14ykpqtw3hw entry point 6 — the reply may carry a server-normalized quantity/unit
                    // (QUO-R-002/003). Adopt it only if the line still holds what was sent: a slow reply for
                    // 100.2 must not overwrite a newer typed 50. A stale reply's weight is for the old quantity
                    // too, so it is left alone; the newer edit has its own re-match scheduled.
                    const current = isUnchangedSince(li, sent);
                    const adopted = adoptableRematchQuantity(li, sent, result);
                    const next: QuotationLineItemDto = {
                      ...li,
                      matchStatus: nextStatus,
                      matchedSalesDescription: result.matchedSalesDescription,
                      matchedItemId: result.matchedItemId,
                      weightKg: current ? result.totalWeightKg : li.weightKg,
                      basePricePerPc: result.basePricePerPc ?? null,
                      averagePricePerPc: result.averagePricePerPc ?? null,
                      possibleMatches:
                        result.possibleMatches?.map((pm) => ({
                          itemId: pm.itemId,
                          lengthM: pm.lengthM,
                          salesDescription: pm.salesDescription,
                          weightPerPcKg: pm.weightPerPcKg,
                        })) ?? [],
                      isMatching: false,
                      matchError: null,
                    };
                    if (adopted) {
                      next.quantity = adopted.quantity;
                      next.unit = adopted.unit;
                      if (result.sourceQuantity != null && result.sourceUnit) {
                        next.originalSpec = withSource(
                          next.originalSpec,
                          result.sourceQuantity,
                          result.sourceUnit === 'kgs' ? 'kgs' : 'pcs',
                        );
                      }
                      if (next.originalSpec) {
                        next.isEdited = differsFromOriginal(next.originalSpec, snapshotFromItem(next));
                      }
                    }
                    return next;
                  }),
                  dirty: true,
                  updatedAt: new Date().toISOString(),
                }
              : d,
          ),
        }));
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Re-match failed';
        toast.error(msg);
        set((state) => ({
          drafts: state.drafts.map((d) =>
            d.id === draftId
              ? {
                  ...d,
                  lineItems: d.lineItems.map((li) =>
                    li.id === itemId ? { ...li, isMatching: false, matchError: msg } : li,
                  ),
                }
              : d,
          ),
        }));
      }
    },

    remove: (id) => get().closeTab(id),

    closeTab: (id) => {
      pendingFiles.delete(id);
      const { drafts, activeId } = get();
      const idx = drafts.findIndex((d) => d.id === id);
      if (idx === -1) return activeId;
      const next = drafts.filter((d) => d.id !== id);
      let nextActive = activeId;
      let nextView = get().activeView;
      if (activeId === id) {
        const neighbor = next[idx] ?? next[idx - 1] ?? null;
        nextActive = neighbor ? neighbor.id : null;
        if (!neighbor) nextView = 'intake';
      }
      set({ drafts: next, activeId: nextActive, activeView: nextView });
      return nextActive;
    },

    activate: (id) => {
      if (get().drafts.some((d) => d.id === id)) {
        set({ activeId: id, activeView: 'draft' });
      }
    },

    clearActive: () => set({ activeId: null }),

    showIntake: () => set({ activeView: 'intake' }),

    clear: () => set({ drafts: [], activeId: null, activeView: 'intake' }),

    startExtraction: (source) => {
      const base = createBlankDraft('intake');
      const hasFiles = source.files.length > 0;
      const trimmedText = source.text.trim();
      const draft: QuotationDraft = {
        ...base,
        extractionStatus: 'processing',
        extractionSourceKind: hasFiles ? 'file' : 'text',
        extractionText: trimmedText.length > 0 ? source.text : null,
        extractionFileName: joinFileNames(source.files),
        // User has loaded source content — work-in-progress. Closing the tab mid-extraction prompts.
        dirty: true,
      };
      if (hasFiles) pendingFiles.set(draft.id, source.files);
      set((state) => ({
        drafts: [...state.drafts, draft],
        activeId: draft.id,
        activeView: 'draft',
      }));
      runExtraction(draft.id, source);
      return draft.id;
    },

    extractIntoDraft: (draftId, source) => {
      const draft = get().drafts.find((d) => d.id === draftId);
      if (!draft || draft.status === QuotationStatus.Submitted) return;

      for (const li of draft.lineItems) {
        const key = rematchKey(draftId, li.id);
        const pending = rematchTimers.get(key);
        if (pending) {
          clearTimeout(pending);
          rematchTimers.delete(key);
        }
      }

      const hasFiles = source.files.length > 0;
      const trimmedText = source.text.trim();
      if (hasFiles) pendingFiles.set(draftId, source.files);
      else pendingFiles.delete(draftId);

      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? {
                ...d,
                extractionStatus: 'processing',
                extractionError: null,
                extractionSource: 'intake',
                extractionSourceKind: hasFiles ? 'file' : 'text',
                extractionText: trimmedText.length > 0 ? source.text : null,
                extractionFileName: joinFileNames(source.files),
                extractionNotice: null,
                dirty: true,
                updatedAt: new Date().toISOString(),
              }
            : d,
        ),
      }));

      runExtraction(draftId, source);
    },

    retryExtraction: (draftId) => {
      const draft = get().drafts.find((d) => d.id === draftId);
      if (!draft || draft.status === QuotationStatus.Submitted) return false;
      const retryFiles = pendingFiles.get(draftId) ?? [];
      const retryText = draft.extractionText ?? '';
      if (retryText.trim().length === 0 && retryFiles.length === 0) return false;
      const source: IntakeSource = { text: retryText, files: retryFiles };

      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId
            ? { ...d, extractionStatus: 'processing', extractionError: null, updatedAt: new Date().toISOString() }
            : d,
        ),
      }));
      runExtraction(draftId, source);
      return true;
    },

    dismissExtractionNotice: (draftId) => {
      set((state) => ({
        drafts: state.drafts.map((d) =>
          d.id === draftId && d.extractionNotice !== null
            ? { ...d, extractionNotice: null, updatedAt: new Date().toISOString() }
            : d,
        ),
      }));
    },
  };
},
    {
      name: 'quotation-draft-tabs',
      version: 2,
      // sessionStorage scopes persistence to the current browser tab and clears on close,
      // so opening the WMS in two browser tabs doesn't clobber each other's state.
      storage: createJSONStorage(() => sessionStorage),
      // 86d32t0xr-followup — only UI navigation prefs are persisted now.
      //
      // Previously this kept `drafts` and `activeId` in sessionStorage to survive
      // accidental tab reloads. That worked but had a bad side effect: opening the
      // same draft in a second browser surfaced THAT tab's stale local copy
      // because the rehydrated cache short-circuited the GET-by-id in
      // QuotationDetailPanel, AND a dirty stale entry could win the loadDraft
      // merge against the server response.
      //
      // Auto-save (86d32t0xr) caps the unsaved-edits window at ~2.5s, so the
      // crash-recovery rationale for persisting `drafts` is no longer
      // load-bearing — the server snapshot is fresher than anything we could
      // recover from sessionStorage. Drop it. Every page load now starts with
      // an empty `drafts` array; the panel's selectedId effect re-fetches from
      // the server. Cross-browser consistency falls out for free.
      //
      // What's still persisted: pure UI navigation prefs that don't represent
      // data and have no server source of truth (column widths, panel
      // collapsed state, last-viewed sub-tab).
      partialize: (state) => ({
        activeView: state.activeView,
        customerColRatio: state.customerColRatio,
        customerPanelCollapsed: state.customerPanelCollapsed,
        reviewSourceColRatio: state.reviewSourceColRatio,
      }),
    },
  ),
);

export function isDraftEmpty(draft: QuotationDraft): boolean {
  return (
    !draft.customerName.trim() &&
    !draft.headMainOffice.trim() &&
    !draft.projectName.trim() &&
    draft.lineItems.length === 0 &&
    draft.extractionStatus !== 'processing'
  );
}

export function draftLabel(draft: QuotationDraft, index: number): string {
  if (draft.customerName.trim()) return draft.customerName.trim();
  if (draft.extractionStatus === 'processing') return 'Extracting…';
  return `Untitled #${index + 1}`;
}
